import React, { useEffect, useRef, useState } from 'react';
import { X, Download, ShieldCheck } from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';

const QRCodeModal = ({ qrData, deviceName, onClose }) => {
  const qrRef = useRef(null);
  const [qrCode, setQrCode] = useState(null);

  useEffect(() => {
    if (!qrData) return;

    const qr = new QRCodeStyling({
      width: 300, // Daha da büyütüldü (eskisi 250'ydi)
      height: 300,
      data: qrData,
      margin: 5,
      qrOptions: {
        typeNumber: 0,
        mode: "Byte",
        errorCorrectionLevel: "H"
      },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.35,
        margin: 5,
        crossOrigin: "anonymous",
      },
      dotsOptions: {
        color: "#991b1b",
        type: "rounded"
      },
      backgroundOptions: {
        color: "#ffffff",
      },
      cornersSquareOptions: {
        color: "#991b1b",
        type: "extra-rounded"
      },
      cornersDotOptions: {
        color: "#dc2626",
        type: "dot"
      },
      image: "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 60' width='200' height='60'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23EF4444' /%3E%3Cstop offset='100%25' stop-color='%23B91C1C' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='200' height='60' rx='15' fill='white' /%3E%3Crect x='2' y='2' width='196' height='56' rx='13' fill='url(%23g)' /%3E%3Cpath d='M30 15 L36 28 L50 30 L40 40 L42 54 L30 47 L18 54 L20 40 L10 30 L24 28 Z' fill='white' /%3E%3Ctext x='125' y='32' dominant-baseline='central' text-anchor='middle' font-family='sans-serif' font-weight='900' font-size='22' fill='white' letter-spacing='1'%3EXpassPDKS%3C/text%3E%3C/svg%3E",
    });

    setQrCode(qr);

    if (qrRef.current) {
      qrRef.current.innerHTML = "";
      qr.append(qrRef.current);
    }
  }, [qrData]);

  if (!qrData) return null;

  const handleDownload = async () => {
    if (!qrCode) return;
    
    // QR kodun saf SVG metnini al
    const rawSvgBlob = await qrCode.getRawData("svg");
    const rawSvgText = await rawSvgBlob.text();
    
    // XML declaration'ı temizle (Çünkü wrapper SVG'nin içine gömüyoruz)
    const cleanSvgText = rawSvgText.replace(/<\?xml.*?\?>/gi, '').replace(/<!DOCTYPE.*?>/gi, '');
    
    const displayName = deviceName || "Bilinmeyen Cihaz";

    // SVG'yi sarmalayarak, altına cihaz adını ekle (Seri numarasını kaldırdık)
    // Genişlik 360, Yükseklik 420
    const wrapperSvg = `<?xml version="1.0" encoding="utf-8"?>
      <svg xmlns="http://www.w3.org/2000/svg" width="360" height="420" viewBox="0 0 360 420" style="background-color: #ffffff;">
        <rect width="360" height="420" fill="#ffffff" />
        <g transform="translate(30, 30)">
          ${cleanSvgText}
        </g>
        <text x="180" y="380" font-family="sans-serif" font-weight="bold" font-size="24" fill="#991b1b" text-anchor="middle">
          ${displayName}
        </text>
      </svg>
    `;
    
    const finalBlob = new Blob([wrapperSvg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(finalBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Xpass-QR-${displayName.replace(/\s+/g, '-')}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm relative overflow-hidden border border-gray-100 dark:border-gray-700">
        
        {/* Top Accent Gradient */}
        <div className="h-2 w-full bg-gradient-to-r from-red-500 via-rose-500 to-orange-500" />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors bg-gray-100 dark:bg-gray-700 rounded-full p-1"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-red-50 dark:bg-red-900/30 p-3 rounded-full mb-3">
              <ShieldCheck className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center">
              Xpass Cihaz Kartı
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-1">
              Bu QR kodu cihaza okutarak geçiş yapabilirsiniz.
            </p>
          </div>

          <div className="relative flex justify-center items-center p-4 bg-gradient-to-b from-red-50 to-white rounded-2xl border border-red-100 dark:border-gray-600 shadow-inner">
            {/* Corner Markers for design */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-red-500 rounded-tl-xl opacity-70" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-red-500 rounded-tr-xl opacity-70" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-red-500 rounded-bl-xl opacity-70" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-red-500 rounded-br-xl opacity-70" />

            <div ref={qrRef} className="rounded-xl overflow-hidden" />
          </div>

          <div className="mt-6 flex flex-col items-center">
            {deviceName && (
              <div className="mb-4 w-full text-center">
                <span className="font-bold text-xl text-red-800 dark:text-red-400 block">{deviceName}</span>
              </div>
            )}

            <button
              onClick={handleDownload}
              className="w-full flex items-center justify-center px-5 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:from-red-700 hover:to-rose-700 transition-all font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <Download className="w-5 h-5 mr-2" />
              Yüksek Kalitede İndir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
